import { File as FileIcon, FileText, Music, type LucideIcon } from 'lucide-react';
import type { MessageAttachment } from '@/entities/ticket';

export type AttachmentKind = 'image' | 'gif' | 'video' | 'audio' | 'document' | 'file';

const DOC_MIME_RE =
  /^(application\/(pdf|msword|rtf|vnd\.openxmlformats|vnd\.ms-|vnd\.oasis|vnd\.apple)|text\/)/;
const DOC_EXT_RE = /\.(pdf|docx?|xlsx?|pptx?|txt|rtf|csv|odt|ods|odp|md|pages|numbers|key)$/i;

export function attachmentKind(mimeType: string, fileName = ''): AttachmentKind {
  const m = (mimeType || '').toLowerCase();
  if (m === 'image/svg+xml') return 'file';
  if (m === 'image/gif') return 'gif';
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (DOC_MIME_RE.test(m) || DOC_EXT_RE.test(fileName)) return 'document';
  return 'file';
}

export function attachmentIcon(kind: AttachmentKind): LucideIcon {
  if (kind === 'audio') return Music;
  if (kind === 'document') return FileText;
  return FileIcon;
}

export function isMediaKind(k: AttachmentKind): boolean {
  return k === 'image' || k === 'gif' || k === 'video';
}

export interface GroupedAttachments<T extends MessageAttachment = MessageAttachment> {
  media: T[];
  audio: T[];
  documents: T[];
  files: T[];
}

export function groupAttachments<T extends MessageAttachment>(list: T[]): GroupedAttachments<T> {
  const g: GroupedAttachments<T> = { media: [], audio: [], documents: [], files: [] };
  for (const a of list) {
    const k = attachmentKind(a.mimeType, a.fileName);
    if (isMediaKind(k)) g.media.push(a);
    else if (k === 'audio') g.audio.push(a);
    else if (k === 'document') g.documents.push(a);
    else g.files.push(a);
  }
  return g;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

const URL_RE = /https?:\/\/[^\s<]+[^\s<.,:;"')\]]/gi;

export function extractUrls(text: string | null | undefined): string[] {
  if (!text) return [];
  return Array.from(text.matchAll(URL_RE), (m) => m[0]);
}

export interface TextSegment {
  type: 'text' | 'link';
  value: string;
}
export function linkifySegments(text: string): TextSegment[] {
  if (!text) return [];
  const segs: TextSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    if (start > last) segs.push({ type: 'text', value: text.slice(last, start) });
    segs.push({ type: 'link', value: m[0] });
    last = start + m[0].length;
  }
  if (last < text.length) segs.push({ type: 'text', value: text.slice(last) });
  return segs;
}
